import { useMemo } from "react";
import {
  useNavigate,
  useParams,
  createFileRoute,
} from "@tanstack/react-router";
import { format } from "date-fns";
import { Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DocViewer } from "@growing/editor";
import "@growing/editor/styles.css";
import { createDocAssetAdapter } from "@growing/api";
import { apiClient } from "@/lib/api/client";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";

// FSD imports
import { useNotice, useDeleteNotice } from "@/features/notice";
import { NoticeBadge, isCategoryObject } from "@/entities/notice";

function NoticeDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams({ strict: false }) as { id: string };
  const { data: notice, isLoading } = useNotice(id);
  const deleteNotice = useDeleteNotice();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  // 본문 이미지의 조회 URL 발급 (presigned, 열 때마다 새로 받는다)
  const assetAdapter = useMemo(() => createDocAssetAdapter(apiClient), []);

  const handleDelete = () => {
    setAlertDialogState({
      title: "공지사항 삭제",
      description: "정말 삭제하시겠습니까?",
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deleteNotice.mutateAsync(id);
          toast.success("삭제되었습니다.");
          navigate({ to: "/notice" });
        } catch {
          toast.error("삭제에 실패했습니다.");
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  return (
    <PageContainer>
      <Header
        title="공지사항"
        right={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate({ to: `/notice/${id}/edit` })}
            >
              <Edit className="size-4 mr-2" />
              수정
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="size-4 mr-2" />
              삭제
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 border-b pb-6 mb-6">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-12" />
              <Skeleton className="h-6 w-16" />
            </div>
            <Skeleton className="h-9 w-2/3" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      ) : notice ? (
        <div className="flex flex-col gap-6">
          {/* 헤더 */}
          <div className="flex flex-col gap-4 border-b pb-6 mb-6">
            <div className="flex items-center gap-2">
              {notice.isPinned && (
                <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded">
                  공지
                </span>
              )}
              {isCategoryObject(notice.category) && (
                <NoticeBadge
                  name={notice.category.name}
                  color={notice.category.color}
                />
              )}
            </div>
            <h1 className="text-3xl font-bold">{notice.title}</h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span>{notice.createdBy.name}</span>
              <span>·</span>
              <span>
                {format(new Date(notice.createdAt), "yyyy.MM.dd HH:mm")}
              </span>
              {notice.lastModifiedBy && (
                <>
                  <span>·</span>
                  <span>수정: {notice.lastModifiedBy.name}</span>
                  <span>
                    {format(new Date(notice.updatedAt), "yyyy.MM.dd HH:mm")}
                  </span>
                </>
              )}
              <span>·</span>
              <span>조회 {notice.viewCount}</span>
            </div>
          </div>

          {/* 본문 */}
          <div className="mb-6">
            <DocViewer value={notice.doc} adapter={assetAdapter} />
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center py-12">
          <p className="text-muted-foreground">공지사항을 찾을 수 없습니다.</p>
        </div>
      )}
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/notice/$id/")({
  component: NoticeDetailPage,
});
